import React, { useState } from "react";
import { Accordion, Badge, Card, Col, Nav, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import ModeratorLogKindDisplay from "@components/Displays/ModeratorLogKindDisplay";
import { DevForm } from "@components/Forms/Fields";
import { DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { humanTimer, parseAndLinkify, url_reverse } from "@components/Utils";

export default function DevelopersDashboardScreen() {
    return (
        <DefaultLayout>
            <Dashboard />
        </DefaultLayout>
    );
}

function Dashboard() {
    const { t } = useTranslation();
    const params = useParams();
    const { apiData } = useAppApi(url_reverse("api:dashboard:list", { dash_type: params.dash_type }));
    const navigate = useNavigate();
    const [showCardInfo, setShowCardInfo] = useState({});

    if (!apiData) return;

    return (
        <Card>
            <Card.Header>
                <Nav
                    variant="tabs"
                    activeKey={params.dash_type}
                    onSelect={(key) => navigate(url_reverse("devs:dashboard", { dash_type: key }))}
                >
                    <Nav.Item>
                        <Nav.Link eventKey="developers">{t("Валидация компании")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="games">{t("Валидация проекта")}</Nav.Link>
                    </Nav.Item>
                </Nav>
            </Card.Header>
            <Card.Body>
                <DashboardCardInfoModal showCardInfo={showCardInfo} setShowCardInfo={setShowCardInfo} />
                {params.dash_type === "developers" && (
                    <Row>
                        {["new", "new_inactive", "approved", "fin_new", "fin_approved", "fin_rejected", "archive"].map(
                            (acc_key) => (
                                <Col key={`col-${params.dash_type}-${acc_key}`}>
                                    <AccordionItem acc_key={acc_key} setShowCardInfo={setShowCardInfo} />
                                </Col>
                            )
                        )}
                    </Row>
                )}
                {params.dash_type === "games" && (
                    <Row>
                        {[
                            "testing",
                            "testing_marketing",
                            "configuration_marketing",
                            "ready_to_pub",
                            "wishlists",
                            "archive",
                        ].map((acc_key) => (
                            <Col key={`col-${params.dash_type}-${acc_key}`}>
                                <AccordionItem acc_key={acc_key} setShowCardInfo={setShowCardInfo} />
                            </Col>
                        ))}
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
}

const date_format = (dt) => {
    if (!dt) return "";
    const a = dt.split(/[- :]/);
    return a[2] + "." + a[1] + "." + a[0] + " " + a[3] + ":" + a[4];
};

const DashboardCardInfoModal = ({ showCardInfo, setShowCardInfo }) => {
    const { apiUrl, setApiData } = useAppContext();
    const { api_get, api_post } = useRequest();

    const show = Boolean(showCardInfo.game || showCardInfo.developer);
    const { t } = useTranslation();
    const { transition, game, history, developer } = showCardInfo;
    if (!developer && !game) {
        return null;
    }

    const sendArchive = (ev, action, entity_id, entity_type, prms) => {
        api_post(ev, url_reverse("api:dashboard:archive"), {
            params: { action, entity_id, entity_type },
            then: (resp) => {
                if (resp.status === "ok") {
                    api_get(null, url_reverse(`api:dashboard:${entity_type}:get`, prms), {
                        then: (resp2) => {
                            setShowCardInfo(resp2);
                            api_get(null, apiUrl, { then: (resp3) => setApiData(resp3) });
                        },
                    });
                }
            },
        });
    };

    const handleClose = () => setShowCardInfo({});

    const transition_title = {
        developer_new: t("Новые заявки"),
        developer_jur: t("Юридическое тестирование"),
        developer_done: t("Заполнено"),
        game_new: t("Конфигурация"),
        game_test_tech: t("Техническое тестирование"),
        game_test_mark: t("Маркетинговое тестирование"),
        game_manual_pub: t("Готово к публикации"),
    };

    const transition_state_variant = (state) => {
        if (["game_done", "game_manual_pub"].includes(transition.state) && state === "game_manual_pub") {
            return "success";
        }
        return transition.state === state ? "success" : "secondary";
    };

    const HistoryList = ({ history_list }) => {
        const commonInfo = useRouteLoaderData("commonInfo");

        return (
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Статус модерации")}</th>
                        <th>{t("Время на этапе")}</th>
                        {game ? (
                            <>
                                <th>{t("Действие")}</th>
                                <th>{t("Комментарий")}</th>
                                <th>{t("Аккаунт")}</th>
                            </>
                        ) : (
                            <th>{t("Дата")}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {history_list?.map((o) => (
                        <tr key={`object_list${o.id}`} style={{ borderColor: o.state_color }}>
                            <td>
                                <b>
                                    <DashboardTransitionStateDisplay val={o.state} />
                                </b>
                            </td>
                            <td>
                                {o.state_color ? (
                                    <Badge variant={o.state_color}>
                                        {humanTimer(o.date_added, o.timer_date ? o.timer_date : commonInfo.now)}
                                    </Badge>
                                ) : (
                                    humanTimer(o.date_added, o.timer_date ? o.timer_date : commonInfo.now)
                                )}
                            </td>
                            {game && (
                                <>
                                    <td>
                                        <Badge variant={o.button_badge_color}>{o.button}</Badge>
                                    </td>
                                    <td>
                                        {o.mlogs?.map((c) => (
                                            <>
                                                <ModeratorLogKindDisplay kind={c.mlog_kind} /> <br />
                                                <div className="show-wrap-text">{parseAndLinkify(c.comment)}</div>
                                                <br />
                                            </>
                                        ))}
                                    </td>
                                </>
                            )}
                            <td>
                                {game ? (
                                    <>
                                        <Link to={url_reverse("admin:account:edit", { acc_id: o.account_id })}>
                                            {o.login}
                                        </Link>{" "}
                                        <br />
                                        {date_format(o.date_state_new)}
                                    </>
                                ) : (
                                    date_format(o.date_added)
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <DevModal
            size="xl"
            show={show}
            onHide={handleClose}
            header={game ? t("Проект") : t("Разработчик")}
            body={
                <>
                    {transition && (
                        <ProgressBar className="mb-3">
                            <ProgressBar
                                striped
                                variant={transition_state_variant("developer_new")}
                                now={20}
                                label={transition_title.developer_new}
                            />
                            <ProgressBar
                                striped
                                variant={transition_state_variant("developer_jur")}
                                now={20}
                                label={transition_title.developer_jur}
                            />
                            <ProgressBar
                                striped
                                variant={transition_state_variant("developer_done")}
                                now={20}
                                label={transition_title.developer_done}
                            />
                            <ProgressBar
                                striped
                                variant={transition_state_variant("game_new")}
                                now={20}
                                label={transition_title.game_new}
                            />
                            <ProgressBar
                                striped
                                variant={transition_state_variant("game_test_tech")}
                                now={20}
                                label={transition_title.game_test_tech}
                            />
                            <ProgressBar
                                striped
                                variant={transition_state_variant("game_test_mark")}
                                now={20}
                                label={transition_title.game_test_mark}
                            />
                            <ProgressBar
                                striped
                                variant={transition_state_variant("game_manual_pub")}
                                now={20}
                                label={transition_title.game_manual_pub}
                            />
                        </ProgressBar>
                    )}
                    <Row>
                        {developer && (
                            <Col md={12}>
                                <Card>
                                    <Card.Header>
                                        <Row>
                                            <Col>
                                                <h3>
                                                    <a
                                                        href={url_reverse("devs:edit", { dev_id: developer.id })}
                                                        target="_blank"
                                                    >
                                                        {developer.name}
                                                    </a>
                                                </h3>
                                            </Col>
                                            <Col align="right">
                                                {t("Логин")}: {developer.login}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                {developer.state === "approved" && (
                                                    <Badge variant="success">{t("Одобрен")}</Badge>
                                                )}
                                            </Col>
                                            <Col align="right">
                                                {t("Дата регистрации")}: {date_format(developer.date_added)}
                                            </Col>
                                        </Row>
                                    </Card.Header>
                                    <Card.Body>
                                        <HistoryList history_list={history} />
                                    </Card.Body>
                                    <Card.Footer>
                                        {(!developer || transition.state !== "archive") && (
                                            <DevForm.Button
                                                variant="primary"
                                                name="to_archive"
                                                onClick={(ev) =>
                                                    sendArchive(ev, "to", developer.id, "developer", {
                                                        developer_id: developer.id,
                                                    })
                                                }
                                            >
                                                {t("В архив дашборда")}
                                            </DevForm.Button>
                                        )}
                                        {developer && transition.state === "archive" && (
                                            <DevForm.Button
                                                variant="primary"
                                                name="from_archive"
                                                onClick={(ev) =>
                                                    sendArchive(ev, "from", developer.id, "developer", {
                                                        developer_id: developer.id,
                                                    })
                                                }
                                            >
                                                {t("Достать из архива дашборда")}
                                            </DevForm.Button>
                                        )}
                                    </Card.Footer>
                                </Card>
                            </Col>
                        )}
                        {game && (
                            <Col md={12}>
                                <Card>
                                    <Card.Header>
                                        <Row>
                                            <Col>
                                                <h3>
                                                    <Link
                                                        to={url_reverse("games:page", { game_id: game.id })}
                                                        target="_blank"
                                                    >
                                                        {game.name}
                                                    </Link>
                                                </h3>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                {t("Разработчик")}:{" "}
                                                <a
                                                    href={url_reverse("devs:edit", {
                                                        dev_id: game.developer_id,
                                                    })}
                                                    target="_blank"
                                                >
                                                    {game.developer_name}
                                                </a>
                                            </Col>
                                        </Row>
                                    </Card.Header>
                                    <Card.Body>
                                        <HistoryList history_list={history} />
                                    </Card.Body>
                                    <Card.Footer>
                                        {game && transition.state !== "archive" && (
                                            <DevForm.Button
                                                variant="primary"
                                                type="submit"
                                                name="to_archive"
                                                onClick={(ev) =>
                                                    sendArchive(ev, "to", game.id, "game", { game_id: game.id })
                                                }
                                            >
                                                {t("В архив дашборда")}
                                            </DevForm.Button>
                                        )}
                                        {game && transition.state === "archive" && (
                                            <DevForm.Button
                                                variant="primary"
                                                name="from_archive"
                                                onClick={(ev) =>
                                                    sendArchive(ev, "from", game.id, "game", { game_id: game.id })
                                                }
                                            >
                                                {t("Достать из архива дашборда")}
                                            </DevForm.Button>
                                        )}
                                    </Card.Footer>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </>
            }
        />
    );
};

const AccordionItem = ({ acc_key, setShowCardInfo }) => {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    const params = useParams();
    const { api_get } = useRequest();

    const type = params.dash_type;
    const col_titles = {
        games: {
            testing: t("Техническая модерация проекта"),
            testing_marketing: t("Маркетинговая модерация"),
            configuration_marketing: t("Конфигурация маркетинг"),
            ready_to_pub: t("Готово к публикации"),
            published: t("Опубликована"),
            wishlists: t("Вишлисты"),
            archive: t("Архив"),
        },
        developers: {
            new: t("Новые заявки"),
            new_inactive: t("Заявка одобрена. Компания неактивна"),
            approved: t("Финансовая модерация. Подготовка"),
            fin_new: t("Финансовая модерация. В процессе"),
            fin_approved: t("Финансовая модерация. Одобрен"),
            fin_approved_inactive: t("Финмодерация пройдена. Разраб неактивен"),
            fin_rejected: t("Финансовая модерация. Отклонен"),
            archive: t("Архив"),
        },
    };
    const col_title = col_titles[params.dash_type][acc_key];
    const objects = apiData[params.dash_type]?.[acc_key];

    const developerCardHandler = (ev_name, developer_id) =>
        api_get(ev_name, url_reverse("api:dashboard:developer:get", { developer_id }), {
            then: (resp) => setShowCardInfo(resp),
        });

    const gameCardHandler = (ev_name, game_id) =>
        api_get(ev_name, url_reverse("api:dashboard:game:get", { game_id }), {
            then: (resp) => setShowCardInfo(resp),
        });

    return (
        <Accordion defaultActiveKey={acc_key === "archive" ? "" : "0"}>
            <Accordion.Toggle as={Card.Header} eventKey="0">
                <div className="dashboard-col-title">
                    {col_title}
                    &nbsp;
                    <span className="badge badge-secondary">{objects?.length}</span>
                </div>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="0">
                <>
                    {objects?.map((o, i) => {
                        if (type === "games") {
                            return (
                                <GameCard
                                    key={`${type}_${acc_key}_${i}`}
                                    game={o}
                                    developer={o}
                                    onClickHandler={gameCardHandler}
                                />
                            );
                        }

                        if (type === "developers") {
                            return (
                                <DeveloperCard
                                    key={`${type}_${acc_key}_${i}`}
                                    developer={o}
                                    onClickHandler={developerCardHandler}
                                />
                            );
                        }

                        return <></>;
                    })}
                </>
            </Accordion.Collapse>
        </Accordion>
    );
};

const DeveloperCard = ({ developer, onClickHandler }) => {
    const { apiIsLoading } = useAppContext();
    const ev_name = `developer_${developer.id}`;
    return (
        <Card className="mt-2" onClick={() => onClickHandler(ev_name, developer.id)}>
            <Card.Body className="dashboard-col-card">
                {apiIsLoading === ev_name ? (
                    <div className="d-flex align-items-center justify-content-center">
                        <Spinner animation="border" variant="primary" role="status" />
                    </div>
                ) : (
                    <>
                        <div>{developer.name}</div>
                        {developer && <div>{date_format(developer.date_added)}</div>}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

const GameCard = ({ game, onClickHandler }) => {
    const { apiIsLoading } = useAppContext();
    const commonInfo = useRouteLoaderData("commonInfo");
    const ev_name = `game_${game.id}`;
    return (
        <Card
            style={{ borderColor: game.state_color }}
            className="mt-2"
            onClick={() => onClickHandler(ev_name, game.id)}
        >
            <Card.Body className="dashboard-col-card">
                {apiIsLoading === ev_name ? (
                    <div className="d-flex align-items-center justify-content-center">
                        <Spinner animation="border" variant="primary" role="status" />
                    </div>
                ) : (
                    <>
                        {game.game_labels?.includes("ИРИ") && <b>ИРИ </b>}
                        {game.game_labels?.includes("VIP") && <b>VIP </b>}
                        <div>{game.name}</div>
                        <div>{game.developer_name}</div>
                        {game && <div>{date_format(game.date_added)}</div>}
                        {game && game.configuration && <div>{humanTimer(game.game_date_state, commonInfo.now)}</div>}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

const DashboardTransitionStateDisplay = ({ val }) => {
    const { t } = useTranslation();
    switch (val) {
        case "developer_new":
            return t("Новый разработчик");
        case "developer_jur":
            return t("Финансовая модерация. Подготовка");
        case "developer_fin_process":
            return t("Финансовая модерация. В процессе");
        case "developer_done":
            return t("Финансовая модерация. Одобрен");
        case "developer_fin_rejected":
            return t("Финансовая модерация. Отклонен");
        case "game_new":
            return t("Конфигурация проекта");
        case "game_test_tech":
            return t("Техническая модерация проекта");
        case "game_test_mark":
            return t("Маркетинговая модерация проекта");
        case "game_conf_mark":
            return t("Конфигурация маркетинг проекта");
        case "game_manual_pub":
            return t("Готово к публикации проекта вручную");
        case "game_done":
            return t("Готово к публикации проекта");
        case "wishlist_new":
            return t("Конфигурация страницы");
        case "wishlist_test_page":
            return t("Техническая модерация страницы");
        case "wishlist_test_mark":
            return t("Маркетинговая модерация страницы");
        case "wishlist_conf_mark":
            return t("Конфигурация маркетинг страницы");
        case "wishlist_done_manual_pub":
            return t("Готово к публикации страницы вручную");
        case "wishlist_done":
            return t("Готово к публикации страницы");
        case "archive":
            return t("Архив");
        default:
            return null;
    }
};
